import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { playerService, User } from '../services/playerService';
import { useUser } from '../contexts/UserContext';

const UserSwitcher: React.FC = memo(() => {
  const { currentUsername, setCurrentUsername, isUserSwitching } = useUser();
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUsername, setSelectedUsername] = useState(currentUsername);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 UserSwitcher: Loading users...');
      const allUsers = await playerService.getAllUsers();
      console.log('✅ UserSwitcher: Users loaded:', allUsers.length);
      setUsers(allUsers);
    } catch (err) {
      console.error('❌ UserSwitcher: Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load users when component mounts
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Update selected username when currentUsername prop changes
  useEffect(() => {
    setSelectedUsername(currentUsername);
  }, [currentUsername]);

  // Find and set current user when users are loaded
  useEffect(() => {
    if (users.length > 0) {
      const user = users.find(u => u.username === currentUsername);
      if (!user) {
        // Default to first user if currentUsername is missing or invalid
        const first = users[0];
        setSelectedUsername(first.username);
        setCurrentUsername(first.username);
        setCurrentUser(first);
      } else {
        setCurrentUser(user);
      }
    } else {
      setCurrentUser(null);
    }
  }, [users, currentUsername]);

  const handleUserChange = useCallback((event: any) => {
    const username = event.target.value;
    if (username === currentUsername) return; // No change needed
    
    console.log(`🔄 UserSwitcher: User selected: ${username}`);
    setSelectedUsername(username);
    setCurrentUsername(username);
  }, [currentUsername, setCurrentUsername]);

  if (loading) {
    return (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          minWidth: 250,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 'bold' }}>
            Switch User
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          minWidth: 250,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 'bold' }}>
            Switch User
          </Typography>
        </Box>
        <Alert severity="error" sx={{ fontSize: '0.75rem', p: 1 }}>
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 2, 
        minWidth: 250,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PersonIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 'bold' }}>
          Switch User
        </Typography>
        {isUserSwitching && (
          <CircularProgress size={16} sx={{ ml: 1 }} />
        )}
      </Box>
      
      <FormControl size="small" fullWidth>
        <InputLabel>Select User</InputLabel>
        <Select
          value={selectedUsername}
          label="Select User"
          onChange={handleUserChange}
          disabled={isUserSwitching}
          sx={{ minWidth: 200 }}
        >
          {users.map((user) => (
            <MenuItem key={user.username} value={user.username}>
              {user.firstName} {user.lastName} ({user.username})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {currentUser && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Currently viewing: {currentUser.firstName} {currentUser.lastName}
        </Typography>
      )}
    </Paper>
  );
});

UserSwitcher.displayName = 'UserSwitcher';

export default UserSwitcher;
